import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { getServiceIcon } from "./iconMap";
import { getCategoryName } from "../utils/getCategoryName";
import type { ServiceCardProps } from "../types";

export function ServiceCard({
  service,
  onClick,
  count,
  showZeroCount = false,
  className,
  subtitle,
  badge,
}: ServiceCardProps) {
  const { t, i18n } = useTranslation();
  const ServiceIcon = getServiceIcon(service.icon);
  const showCount = count !== undefined && (count > 0 || showZeroCount);
  const displayName = getCategoryName(service, i18n.language, t);

  return (
    <button
      onClick={onClick}
      className={cn(
        "bg-card border border-border rounded-xl p-4 sm:p-5 text-left",
        "hover:border-primary/50 hover:shadow-md transition-all group w-full",
        className
      )}>
      <div className="flex items-center justify-between gap-2">
        {/*
          Without `min-w-0` the text cannot shrink below its own width, and the
          card's edge cuts the longer categories off instead of wrapping them
          (BUG-61).
        */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors shrink-0">
            <ServiceIcon className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-foreground">{displayName}</h3>
            {subtitle && <div className="text-sm text-muted-foreground mt-0.5">{subtitle}</div>}
          </div>
        </div>

        {(badge || showCount) && (
          <div className="flex items-center gap-2 shrink-0">
            {badge}
            {showCount && (
              <div
                className={cn(
                  "px-3 py-1 rounded-full text-sm font-medium",
                  count && count > 0
                    ? "bg-green-100 text-green-700"
                    : "bg-muted text-muted-foreground"
                )}>
                {count}
              </div>
            )}
          </div>
        )}
      </div>
    </button>
  );
}
